import React, { useState, useEffect } from 'react';
import { FiZap, FiAlertCircle, FiCheckCircle, FiCpu } from 'react-icons/fi';
import ImageUpload from './components/ImageUpload';
import ImageCrop from './components/ImageCrop';
import BackgroundOptions from './components/BackgroundOptions';
import Preview from './components/Preview';
import Result from './components/Result';
import { processImage, autoProcessImage, getImageUrl, getPassportCountries, getFilenameFromPath } from './services/api';
import './SelfieEditor.css';

/**
 * SelfieEditor Component
 * Manages state and coordinates all components
 */
function SelfieEditor() {
  // State management
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [backgroundType, setBackgroundType] = useState('transparent');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [passportSize, setPassportSize] = useState(false);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countriesList, setCountriesList] = useState([]);
  const [countrySizes, setCountrySizes] = useState({});
  const [loading, setLoading] = useState(false);
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const [outputFilename, setOutputFilename] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processingMode, setProcessingMode] = useState('manual'); // 'manual' or 'auto'
  const [autoCrop, setAutoCrop] = useState(true);

  /**
   * Handle image selection
   */
  const handleImageSelect = (file) => {
    setSelectedImage(file);
    setResultImageUrl(null);
    setOutputFilename(null);
    setError(null);
    setSuccess(false);
  };

  /**
   * Handle background type change
   */
  const handleBackgroundTypeChange = (type) => {
    setBackgroundType(type);
    setResultImageUrl(null);
    setError(null);
  };

  /**
   * Handle background color change
   */
  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
  };

  // Fetch passport countries list on component mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchCountries = async () => {
      try {
        const response = await getPassportCountries();
        if (isMounted && response?.countries) {
          const countryNames = Object.keys(response.countries).sort();
          setCountriesList(countryNames);
          setCountrySizes(response.countries);
        }
      } catch (error) {
        // Only log if component is still mounted and it's not a cancellation
        if (isMounted) {
          console.error('Error loading passport countries:', error);
          // Fallback - use empty list, don't show error to user
        }
      }
    };
    
    fetchCountries();
    
    // Cleanup
    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Handle crop button click
   */
  const handleCropClick = () => {
    if (selectedImage) {
      setShowCropModal(true);
    }
  };

  /**
   * Handle crop complete
   */
  const handleCropComplete = (croppedImageFile) => {
    setSelectedImage(croppedImageFile);
    setShowCropModal(false);
    setResultImageUrl(null);
    setOutputFilename(null);
    setError(null);
    setSuccess(false);
  };

  /**
   * Handle crop cancel
   */
  const handleCropCancel = () => {
    setShowCropModal(false);
  };

  /**
   * Validate form before submission
   */
  const validateForm = () => {
    if (!selectedImage) {
      setError('Please select an image to process');
      return false;
    }

    if (backgroundType === 'color' && !backgroundColor) {
      setError('Please select a background color');
      return false;
    }

    return true;
  };

  /**
   * Process image via API (Manual Mode)
   */
  const handleManualProcess = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setResultImageUrl(null);
    setOutputFilename(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('remove_background', 'true');
      formData.append('background_type', backgroundType);
      formData.append('passport_size', passportSize.toString());

      if (backgroundType === 'color' && backgroundColor) {
        formData.append('background_value', backgroundColor);
      }

      if (selectedCountry) {
        formData.append('country', selectedCountry);
      } else if (customWidth && customHeight) {
        formData.append('custom_width', customWidth);
        formData.append('custom_height', customHeight);
      }

      const response = await processImage(formData);

      if (response.status === 'success') {
        const imageUrl = getImageUrl(response.output_image);
        setResultImageUrl(imageUrl);
        setOutputFilename(getFilenameFromPath(response.output_image));
        setSuccess(true);
      } else {
        setError(response.message || 'Failed to process image');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while processing the image');
      console.error('Error processing image:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Auto process image via API (Auto Mode)
   */
  const handleAutoProcess = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setResultImageUrl(null);
    setOutputFilename(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('background_type', backgroundType);
      formData.append('auto_crop', autoCrop.toString());
      formData.append('auto_resize', 'true');

      if (backgroundType === 'color' && backgroundColor) {
        formData.append('background_value', backgroundColor);
      }

      // Determine target size
      if (selectedCountry) {
        formData.append('target_size', 'country');
        formData.append('country', selectedCountry);
      } else if (customWidth && customHeight) {
        formData.append('target_size', 'custom');
        formData.append('custom_width', customWidth);
        formData.append('custom_height', customHeight);
      } else if (passportSize) {
        formData.append('target_size', 'passport');
      } else {
        formData.append('target_size', 'passport'); // Default
      }

      const response = await autoProcessImage(formData);

      if (response.status === 'success') {
        const imageUrl = getImageUrl(response.output_image);
        setResultImageUrl(imageUrl);
        setOutputFilename(getFilenameFromPath(response.output_image));
        setSuccess(true);
      } else {
        setError(response.message || 'Failed to auto-process image');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while auto-processing the image');
      console.error('Error auto-processing image:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle process button click based on mode
   */
  const handleProcessImage = () => {
    if (processingMode === 'auto') {
      handleAutoProcess();
    } else {
      handleManualProcess();
    }
  };

  /**
   * Reset form to process another image
   */
  const handleReset = () => {
    setSelectedImage(null);
    setBackgroundType('transparent');
    setBackgroundColor('#ffffff');
    setPassportSize(false);
    setCustomWidth('');
    setCustomHeight('');
    setSelectedCountry('');
    setResultImageUrl(null);
    setOutputFilename(null);
    setError(null);
    setSuccess(false);
  };

  // Get selected country size info
  const selectedCountrySize = selectedCountry && countrySizes[selectedCountry];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
      {/* Header */}
      <div className="mb-8 sm:mb-10 lg:mb-12 text-center md:text-left">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-3 sm:mb-4 text-gray-900 dark:text-white">
          Passport Photo Editor
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 font-medium max-w-3xl mx-auto md:mx-0">
          Remove or replace backgrounds from your photos with AI - Perfect for passport & ID photos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
        {/* Left Column - Tools & Image Upload */}
        <div className="lg:col-span-5 space-y-4 lg:space-y-6 order-2 lg:order-1">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 overflow-hidden shadow-xl">
            {/* Processing Mode Toggle */}
            <div className="p-5 border-b-2 border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 via-purple-50 to-blue-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-blue-900/20">
              <div className="flex items-center gap-2 mb-4">
                <FiCpu className="text-blue-600 dark:text-blue-400 text-lg" />
                <span className="text-sm font-bold text-gray-700 dark:text-gray-200">Processing Mode</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setProcessingMode('manual')}
                  className={`py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    processingMode === 'manual'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform scale-105'
                      : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 hover:scale-105'
                  }`}
                >
                  Manual
                </button>
                <button
                  onClick={() => setProcessingMode('auto')}
                  className={`py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 flex items-center justify-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    processingMode === 'auto'
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg transform scale-105'
                      : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 hover:scale-105'
                  }`}
                >
                  <FiZap size={14} /> Auto AI
                </button>
              </div>
              {processingMode === 'auto' && (
                <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 font-medium bg-white/50 dark:bg-slate-800/50 px-3 py-2 rounded-lg">
                  ✨ Auto-crop, resize, and background removal in one click
                </p>
              )}
            </div>

            {/* Image Upload */}
            <div className="p-6">
              <ImageUpload
                onImageSelect={handleImageSelect}
                selectedImage={selectedImage}
                onCropClick={handleCropClick}
              />
            </div>

            {/* Background Options */}
            <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-800/50">
              <BackgroundOptions
                backgroundType={backgroundType}
                onBackgroundTypeChange={handleBackgroundTypeChange}
                onBackgroundColorChange={handleBackgroundColorChange}
                passportSize={passportSize}
                onPassportSizeChange={setPassportSize}
                customWidth={customWidth}
                customHeight={customHeight}
                onCustomWidthChange={setCustomWidth}
                onCustomHeightChange={setCustomHeight}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
                countriesList={countriesList}
              />

              {/* Show selected country dimensions */}
              {selectedCountrySize && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                    {selectedCountry}: {selectedCountrySize.width} x {selectedCountrySize.height} px
                  </p>
                </div>
              )}

              {/* Auto-crop toggle (only in auto mode) */}
              {processingMode === 'auto' && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoCrop}
                      onChange={(e) => setAutoCrop(e.target.checked)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">Auto-Crop to Subject</p>
                      <p className="text-xs text-gray-400">Automatically focus on face/subject</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Process Button */}
            <div className="p-6 border-t-2 border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-800/50">
              <button
                className={`w-full py-4 px-6 rounded-xl font-bold text-white transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus:ring-4 focus:ring-blue-500/50 ${
                  loading || !selectedImage
                    ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                    : processingMode === 'auto'
                      ? 'bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 hover:from-purple-700 hover:via-blue-700 hover:to-purple-700 shadow-purple-500/30'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-blue-500/30'
                }`}
                onClick={handleProcessImage}
                disabled={loading || !selectedImage}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FiZap className="text-lg" /> {processingMode === 'auto' ? 'Auto Process' : 'Process Image'}
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm flex items-start gap-3 shadow-sm">
                  <FiAlertCircle className="mt-0.5 flex-shrink-0" />
                  <span className="font-medium">{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Preview & Result */}
        <div className="lg:col-span-7 space-y-4 lg:space-y-6 order-1 lg:order-2">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
            {!resultImageUrl ? (
              <div className="p-4 sm:p-6 min-h-[300px] sm:min-h-[400px] flex flex-col">
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                    Live Preview
                  </h3>
                </div>
                <div className="flex-1 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900/50 dark:to-slate-800/50 min-h-[250px] sm:min-h-[300px]">
                  <Preview
                    selectedImage={selectedImage}
                    backgroundType={backgroundType}
                    backgroundColor={backgroundColor}
                    resultImageUrl={resultImageUrl}
                    passportSize={passportSize}
                    customWidth={customWidth}
                    customHeight={customHeight}
                    selectedCountry={selectedCountry}
                  />
                </div>
              </div>
            ) : (
              <div className="p-4 sm:p-6 min-h-[300px] sm:min-h-[400px]">
                <Result
                  resultImageUrl={resultImageUrl}
                  outputFilename={outputFilename}
                  onReset={handleReset}
                />
              </div>
            )}
          </div>

          {success && !resultImageUrl && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 flex items-center gap-3 shadow-sm">
              <FiCheckCircle className="text-xl flex-shrink-0" />
              <span className="font-bold">Image processed successfully!</span>
            </div>
          )}
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && selectedImage && (
        <ImageCrop
          imageFile={selectedImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

export default SelfieEditor;
